import {
  RefreshCw,
  Building2,
  Users,
  TrendingUp,
  Lightbulb,
  ExternalLink,
  Globe,
  Target,
  Shield,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import type { CompAnalysisJob, CompAnalysisCompetitor, CompAnalysisRecommendation } from '@/services/competitor-analysis/types'
import { useState } from 'react'

interface CompetitorAnalysisResultsProps {
  job: CompAnalysisJob
  onBack: () => void
  onReanalyze: () => void
}

export default function CompetitorAnalysisResults({ job, onBack: _onBack, onReanalyze }: CompetitorAnalysisResultsProps) {
  const result = job.result

  if (!result) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-tf-text-muted">No results available</p>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={onReanalyze}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/10 text-purple-500 hover:bg-purple-500/20 transition-colors text-sm font-medium"
        >
          <RefreshCw className="w-4 h-4" />
          Re-analyze
        </button>
      </div>

      {/* Summary */}
      {result.summary && (
        <div className="mb-6 px-4 py-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border border-purple-500/20">
          <p className="text-sm text-tf-text">{result.summary}</p>
        </div>
      )}

      {/* Brand Profile Card */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-tf-text mb-3 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-purple-500" />
          Your Brand Profile
        </h2>
        <div className="rounded-xl bg-tf-bg-card border border-tf-border p-4">
          <div className="grid grid-cols-2 gap-4">
            <InfoItem label="Industry" value={result.brand.industry} />
            <InfoItem label="Target Customer" value={result.brand.target_customer} />
            <InfoItem label="Pricing Tier" value={result.brand.pricing_tier} />
            <InfoItem label="Market Position" value={result.brand.market_position} />
          </div>
          {result.brand.value_proposition && (
            <div className="mt-4 pt-4 border-t border-tf-border">
              <p className="text-xs text-tf-text-muted mb-1">Value Proposition</p>
              <p className="text-sm text-tf-text">{result.brand.value_proposition}</p>
            </div>
          )}
        </div>
      </div>

      {/* Competitors */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-tf-text mb-3 flex items-center gap-2">
          <Users className="w-5 h-5 text-purple-500" />
          Discovered Competitors
        </h2>
        <div className="space-y-3">
          {result.competitors.map((competitor) => (
            <CompetitorCard key={competitor.id} competitor={competitor} />
          ))}
        </div>
      </div>

      {/* Market Insights */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-tf-text mb-3 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-purple-500" />
          Market Insights
        </h2>
        <div className="rounded-xl bg-tf-bg-card border border-tf-border p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            {result.market_insights.market_size && (
              <InfoItem label="Market Size" value={result.market_insights.market_size} />
            )}
            {result.market_insights.growth_trend && (
              <InfoItem
                label="Growth Trend"
                value={result.market_insights.growth_trend}
                valueClassName={cn(
                  result.market_insights.growth_trend === 'growing' && 'text-emerald-500',
                  result.market_insights.growth_trend === 'declining' && 'text-red-500',
                )}
              />
            )}
            <InfoItem
              label="Competition Level"
              value={result.market_insights.competitive_level}
              valueClassName={cn(
                result.market_insights.competitive_level === 'intense' && 'text-red-500',
                result.market_insights.competitive_level === 'high' && 'text-amber-500',
              )}
            />
          </div>

          {result.market_insights.key_trends && result.market_insights.key_trends.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-tf-text-muted mb-2">Key Trends</p>
              <div className="flex flex-wrap gap-2">
                {result.market_insights.key_trends.map((trend, i) => (
                  <span key={i} className="px-2 py-1 rounded-full bg-blue-500/10 text-blue-500 text-xs">
                    {trend}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {result.market_insights.opportunities && result.market_insights.opportunities.length > 0 && (
              <div>
                <p className="text-xs text-tf-text-muted mb-2 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-emerald-500" />
                  Opportunities
                </p>
                <ul className="space-y-1">
                  {result.market_insights.opportunities.map((opp, i) => (
                    <li key={i} className="text-sm text-tf-text">• {opp}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.market_insights.threats && result.market_insights.threats.length > 0 && (
              <div>
                <p className="text-xs text-tf-text-muted mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                  Threats
                </p>
                <ul className="space-y-1">
                  {result.market_insights.threats.map((threat, i) => (
                    <li key={i} className="text-sm text-tf-text">• {threat}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {result.recommendations && result.recommendations.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-tf-text mb-3 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-purple-500" />
            Recommendations
          </h2>
          <div className="space-y-3">
            {result.recommendations.map((rec, i) => (
              <RecommendationCard key={i} recommendation={rec} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function InfoItem({ label, value, valueClassName }: { label: string; value: string; valueClassName?: string }) {
  return (
    <div>
      <p className="text-xs text-tf-text-muted">{label}</p>
      <p className={cn('text-sm font-medium text-tf-text capitalize', valueClassName)}>{value}</p>
    </div>
  )
}

function CompetitorCard({ competitor }: { competitor: CompAnalysisCompetitor }) {
  const [expanded, setExpanded] = useState(false)

  const categoryColors: Record<string, string> = {
    'Direct': 'bg-red-500/10 text-red-500',
    'Indirect': 'bg-amber-500/10 text-amber-500',
    'Aspirational': 'bg-purple-500/10 text-purple-500',
    'Industry Leader': 'bg-blue-500/10 text-blue-500',
    'Emerging': 'bg-emerald-500/10 text-emerald-500',
    'Alternative': 'bg-slate-500/10 text-slate-400',
  }

  return (
    <div className="rounded-xl bg-tf-bg-card border border-tf-border overflow-hidden">
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-indigo-500/20 flex items-center justify-center">
          <Globe className="w-5 h-5 text-purple-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-tf-text">{competitor.name}</p>
            <span className={cn('px-2 py-0.5 rounded-full text-xs', categoryColors[competitor.category] || 'bg-tf-bg-muted text-tf-text-muted')}>
              {competitor.category}
            </span>
          </div>
          {competitor.website && (
            <p className="text-xs text-tf-text-muted truncate">{competitor.website}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-tf-text-muted">
            {Math.round(competitor.confidence_score * 100)}% match
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 text-tf-text-muted" /> : <ChevronDown className="w-4 h-4 text-tf-text-muted" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-tf-border mt-2">
          <div className="pt-4 space-y-3">
            {competitor.description && (
              <p className="text-sm text-tf-text-muted">{competitor.description}</p>
            )}

            <div className="grid grid-cols-2 gap-3">
              {competitor.pricing_tier && (
                <InfoItem label="Pricing Tier" value={competitor.pricing_tier} />
              )}
              {competitor.estimated_size && (
                <InfoItem label="Company Size" value={competitor.estimated_size} />
              )}
            </div>

            {competitor.strengths && competitor.strengths.length > 0 && (
              <div>
                <p className="text-xs text-tf-text-muted mb-1 flex items-center gap-1">
                  <Shield className="w-3 h-3 text-emerald-500" />
                  Strengths
                </p>
                <div className="flex flex-wrap gap-1">
                  {competitor.strengths.map((s, i) => (
                    <span key={i} className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 text-xs">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {competitor.weaknesses && competitor.weaknesses.length > 0 && (
              <div>
                <p className="text-xs text-tf-text-muted mb-1 flex items-center gap-1">
                  <Target className="w-3 h-3 text-amber-500" />
                  Weaknesses
                </p>
                <div className="flex flex-wrap gap-1">
                  {competitor.weaknesses.map((w, i) => (
                    <span key={i} className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 text-xs">
                      {w}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {competitor.website && (
              <a
                href={competitor.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-purple-500 hover:text-purple-400"
              >
                Visit website <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function RecommendationCard({ recommendation }: { recommendation: CompAnalysisRecommendation }) {
  const priorityColors: Record<string, string> = {
    'high': 'bg-red-500/10 text-red-500 border-red-500/20',
    'medium': 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    'low': 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  }

  const categoryIcons: Record<string, typeof Lightbulb> = {
    'positioning': Target,
    'messaging': Lightbulb,
    'targeting': Users,
    'product': Building2,
    'pricing': TrendingUp,
  }

  const Icon = categoryIcons[recommendation.category] || Lightbulb

  return (
    <div className={cn(
      'rounded-xl border p-4',
      priorityColors[recommendation.priority] || 'bg-tf-bg-card border-tf-border'
    )}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-white/10">
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm font-medium">{recommendation.title}</p>
            <span className="px-2 py-0.5 rounded-full text-xs bg-white/10 capitalize">
              {recommendation.priority} priority
            </span>
          </div>
          <p className="text-xs opacity-80">{recommendation.description}</p>
        </div>
      </div>
    </div>
  )
}
